<template>
  <!-- EcountEtl 트랜잭션 목록 (SPEC-CHINA-ECOUNT-PRODUCTS-TRANSACTIONS-DASHBOARD-001 + SPEC-ECOUNT-FE-POLISH-001 Phase B-2) -->
  <div class="page-container-v2">
    <div class="page-header">
      <span class="page-title">{{ $t('ecountEtl_transactions_title') }}</span>
      <a-tooltip :title="$t('ecountEtl_transactions_collection')" placement="bottom">
        <QuestionCircleOutlined class="title-tip-icon" />
      </a-tooltip>
      <div style="display: flex; align-items: center; gap: 8px; margin-left: auto;">
        <timezone-toggle :current-tz="currentTz" @toggle="toggleTz" />
        <!-- SPEC-CHINA-ECOUNT-TX-LINK-001 파트 B: 연결키 설명 모달 버튼 -->
        <a-button size="small" type="default" @click="showTxLinkModal = true">
          {{ $t('ecountTxLink_modal_title') }}
        </a-button>
        <a-button size="small" @click="showShortcutHelp = true">?</a-button>
      </div>
    </div>

    <!-- 거래 대시보드 (SPEC-CHINA-ECOUNT-PRODUCTS-TRANSACTIONS-DASHBOARD-001) -->
    <transactions-dashboard
      :stats-data="statsData"
      :loading="statsLoading"
      :error-msg="statsErrorMsg"
      :selected-customer-codes="filters.customerCodes"
      :active-product-code="filters.ecountProductCode"
      :active-transaction-type="filters.transactionType"
      @retry="fetchStats"
      @chart-filter="onChartFilter"
    />

    <!-- 검색 필터 -->
    <a-card style="margin-bottom: 16px;">
      <a-form layout="inline" class="dealer-filter-form" :model="filters" @finish="onSearch">
        <a-form-item>
          <a-input
            ref="searchInput"
            v-model:value="filters.keyword"
            :placeholder="$t('ecountEtl_transactions_search_placeholder')"
            allow-clear
            style="min-width: 200px;"
          />
        </a-form-item>
        <!-- REQ-FE-T-001: 기존 customerCode input 제거 / REQ-FE-T-002: customerCodes multi-select 추가 -->
        <a-form-item :label="$t('ecountEtl_transactions_filter_customer')">
          <a-select
            v-model:value="filters.customerCodes"
            mode="multiple"
            :placeholder="$t('ecountEtl_transactions_filter_customer_ph')"
            :options="customerOptions"
            :filter-option="filterCustomerOption"
            :disabled="customersLoadError"
            :loading="customersLoading"
            allow-clear
            show-search
            style="min-width: 220px; max-width: 360px;"
          >
            <template v-if="customersLoadError" #notFoundContent>
              <span style="color: #ff4d4f;">{{ $t('ecountEtl_transactions_customer_load_error') }}</span>
            </template>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('ecountEtl_runs_filter_status')">
          <a-select
            v-model:value="filters.status"
            allow-clear
            style="min-width: 130px;"
          >
            <a-select-option value="success">Success</a-select-option>
            <a-select-option value="failed">Failed</a-select-option>
          </a-select>
        </a-form-item>
        <!-- REQ-FE-LINK-006: lotNumber 검색 + prefix 토글 (SPEC-CHINA-LOT-BATCH-TRACEABILITY-001) -->
        <a-form-item :label="$t('lotTrace_lotNumber_label')">
          <a-input
            v-model:value="filters.lotNumber"
            :placeholder="$t('lotTrace_col_lotNumber')"
            allow-clear
            style="width: 180px;"
          />
        </a-form-item>
        <a-form-item>
          <a-checkbox v-model:checked="filters.lotNumberPrefix">{{ $t('lotTrace_prefix_toggle') }}</a-checkbox>
        </a-form-item>
        <a-form-item :label="$t('ecountEtl_runs_filter_dateRange')">
          <a-range-picker v-model:value="filters.dateRange" style="width: 240px;" />
        </a-form-item>
        <a-form-item class="filter-actions">
          <a-space>
            <a-button type="primary" html-type="submit" :loading="loading">
              {{ $t('ecountEtl_common_search') }}
            </a-button>
            <a-button @click="onReset">{{ $t('ecountEtl_common_reset') }}</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- chart 클릭 필터 chip 표시 -->
    <div
      v-if="filters.customerCodes.length > 0 || filters.ecountProductCode || filters.transactionType"
      style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px;"
    >
      <a-tag
        v-for="code in filters.customerCodes"
        :key="code"
        closable
        color="orange"
        @close="removeCustomerCode(code)"
      >
        {{ customerLabel(code) }}
      </a-tag>
      <a-tag
        v-if="filters.ecountProductCode"
        closable
        color="green"
        @close="filters.ecountProductCode = ''"
      >
        {{ $t('ecountEtl_chip_product') }}: {{ filters.ecountProductCode }}
      </a-tag>
      <a-tag
        v-if="filters.transactionType"
        closable
        color="blue"
        @close="filters.transactionType = ''"
      >
        {{ $t('ecountEtl_chip_txType') }}: {{ filters.transactionType }}
      </a-tag>
      <a-button size="small" @click="onClearFilters">{{ $t('ecountEtl_transactions_clear_filters') }}</a-button>
    </div>

    <!-- 오류 -->
    <a-alert
      v-if="error"
      type="error"
      :message="error"
      style="margin-bottom: 16px;"
      show-icon
      closable
      @close="error = null"
    />

    <!-- 툴바: 총건수 + pageSize -->
    <div class="toolbar-row">
      <span class="total-count">
        {{ $t('ecountEtl_common_total') }}: {{ formatNumber(pagination.total) }}
      </span>
      <div class="toolbar-controls">
        <a-select v-model:value="pageSize" size="small" style="width: 100px;" @change="onToolbarPageSizeChange">
          <a-select-option :value="10">10 / {{ $t('ecountEtl_common_page') }}</a-select-option>
          <a-select-option :value="20">20 / {{ $t('ecountEtl_common_page') }}</a-select-option>
          <a-select-option :value="50">50 / {{ $t('ecountEtl_common_page') }}</a-select-option>
          <a-select-option :value="100">100 / {{ $t('ecountEtl_common_page') }}</a-select-option>
        </a-select>
      </div>
    </div>

    <!-- 데스크탑: a-table (가로 스크롤) -->
    <a-table
      v-if="!isMobileView"
      :data-source="transactions"
      :columns="columns"
      :loading="loading"
      :pagination="false"
      :row-class-name="rowClassName"
      row-key="_id"
      size="small"
      :scroll="{ x: 3620 }"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'lotNumber'">
          <!-- REQ-FE-LINK-001: lotNumber 셀 hyperlink → /lot-trace/:value (SPEC-CHINA-LOT-BATCH-TRACEABILITY-001) -->
          <a-button
            v-if="record.lotNumber"
            type="link"
            size="small"
            style="padding: 0; min-height: 44px; height: auto;"
            @click="navigateToLotTrace(record.lotNumber)"
          >{{ record.lotNumber }}</a-button>
          <span v-else>-</span>
        </template>
        <template v-else-if="['transactionDate','inputAt','createdAt','updatedAt','mfgDate'].includes(column.key)">
          {{ formatDateTime(record[column.dataIndex]) }}
        </template>
        <template v-else-if="column.key === '_orphan'">
          <a-tag v-if="record._orphan" color="warning">{{ $t('ecountEtl_transactions_orphanBadge') }}</a-tag>
          <span v-else>-</span>
        </template>
        <template v-else-if="column.key === 'isDeleted'">
          <a-tag v-if="record.isDeleted" color="red">✗</a-tag>
          <span v-else>-</span>
        </template>
        <template v-else-if="column.key === 'lotFlowBtn'">
          <!-- 유통 흐름 보기 버튼: lot/GTIN으로 /lot-status 진입 -->
          <a-button
            v-if="record.lotNumber || record.gtinCode"
            type="primary"
            size="small"
            ghost
            style="min-height: 44px; height: auto;"
            @click="navigateToLotFlow(record.lotNumber, record.gtinCode)"
          >{{ $t('lotFlow_actions_viewFlow') }}</a-button>
          <span v-else>-</span>
        </template>
        <template v-else-if="column.key === 'gtinChecksumValid'">
          <a-tag v-if="record.gtinChecksumValid === true" color="green">✓</a-tag>
          <a-tag v-else-if="record.gtinChecksumValid === false" color="red">✗</a-tag>
          <span v-else>-</span>
        </template>
        <template v-else-if="column.key === 'transactionDirection'">
          <a-tag :color="record.transactionDirection === 'INBOUND' ? 'green' : 'blue'">{{ record.transactionDirection || '-' }}</a-tag>
        </template>
        <template v-else-if="column.key === 'transactionTypeEnum'">
          <a-tag>{{ record.transactionTypeEnum || '-' }}</a-tag>
        </template>
        <template v-else-if="column.key === 'qty'">
          {{ formatNumber(record.qty) }}
        </template>
        <template v-else-if="column.key === 'lineNo'">
          {{ record.lineNo ?? '-' }}
        </template>
        <!-- SPEC-CHINA-ECOUNT-TX-LINK-001 파트 C: matchKey 컬럼 -->
        <template v-else-if="column.key === 'matchKey'">
          <span
            v-if="record.serialLotMatchKey"
            class="match-key-cell"
            :title="record.serialLotMatchKey"
          >{{ record.serialLotMatchKey }}</span>
          <span v-else style="color: #bfbfbf;">-</span>
        </template>
        <!-- SPEC-CHINA-ECOUNT-TX-LINK-001 파트 C: 거래상세 연결 버튼 -->
        <template v-else-if="column.key === 'linkSalesLineBtn'">
          <a-button
            v-if="record.voucherNo != null && record.lineNo != null"
            type="link"
            size="small"
            style="padding: 0;"
            @click="navigateToSalesLine(record)"
          >{{ $t('ecountEtl_transactions_linkSalesLine_btn') }}</a-button>
          <span v-else style="color: #bfbfbf;">-</span>
        </template>
      </template>
    </a-table>

    <!-- 모바일: 카드뷰 -->
    <div v-else class="mobile-card-list">
      <div v-if="transactions.length === 0 && !loading" class="empty-state">
        <a-empty :description="$t('ecountEtl_common_empty')" />
      </div>
      <div
        v-for="record in transactions"
        :key="record._id"
        class="mobile-card"
        :class="{ 'mobile-card-orphan': record._orphan }"
      >
        <div class="card-header">
          <div class="card-header-left">
            <span class="card-id">#{{ record.voucherNo ?? '-' }}</span>
            <a-tag :color="record.transactionDirection === 'INBOUND' ? 'green' : 'blue'">{{ record.transactionDirection || '-' }}</a-tag>
            <a-tag>{{ record.transactionTypeEnum || '-' }}</a-tag>
          </div>
          <a-tag v-if="record._orphan" color="warning" style="margin: 0;">
            {{ $t('ecountEtl_transactions_orphanBadge') }}
          </a-tag>
        </div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_transactionDate') }}</span><span class="card-value">{{ formatDateTime(record.transactionDate) }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_transactionType') }}</span><span class="card-value">{{ record.transactionType || '-' }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_ecountProductCode') }}</span><span class="card-value mono">{{ record.ecountProductCode || '-' }}</span></div>
        <div class="card-field card-product-desc"><span class="card-label">{{ $t('ecountEtl_transactions_col_productDescription') }}</span><span class="card-value">{{ record.productDescription || '-' }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_gtinCode') }}</span><a-button v-if="record.gtinCode" type="link" size="small" class="card-gtin-btn card-value mono" @click="drillToProduct(record.gtinCode)">{{ record.gtinCode }}</a-button><span v-else class="card-value mono">-</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_gtin14') }}</span><span class="card-value mono">{{ record.gtin14 || '-' }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_gs1CompanyPrefix3') }}</span><span class="card-value mono">{{ record.gs1CompanyPrefix3 || '-' }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_gtinChecksumValid') }}</span><span class="card-value"><a-tag v-if="record.gtinChecksumValid === true" color="green">✓</a-tag><a-tag v-else-if="record.gtinChecksumValid === false" color="red">✗</a-tag><span v-else>-</span></span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_mfgDate') }}</span><span class="card-value">{{ record.mfgDate ? formatDateTime(record.mfgDate) : '-' }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_lotNumber') }}</span>
          <a-button v-if="record.lotNumber" type="link" size="small" class="card-matchkey-btn card-value mono" @click="navigateToLotTrace(record.lotNumber)">{{ record.lotNumber }}</a-button>
          <span v-else class="card-value mono">-</span>
        </div>
        <!-- 유통 흐름 보기 버튼 (모바일 카드) -->
        <div v-if="record.lotNumber || record.gtinCode" class="card-field" style="justify-content: flex-end; padding-top: 8px;">
          <a-button
            type="primary"
            size="small"
            ghost
            @click="navigateToLotFlow(record.lotNumber, record.gtinCode)"
          >{{ $t('lotFlow_actions_viewFlow') }}</a-button>
        </div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_qty') }}</span><span class="card-value">{{ formatNumber(record.qty) }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_customerName') }}</span><span class="card-value">{{ record.customerName || '-' }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_customerCode') }}</span><span class="card-value mono">{{ record.customerCode || '-' }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_employeeCode') }}</span><span class="card-value">{{ record.employeeCode || '-' }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_inputUserId') }}</span><span class="card-value">{{ record.inputUserId || '-' }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_ioDateNo') }}</span><span class="card-value mono">{{ record.ioDateNo || '-' }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_lineNo') }}</span><span class="card-value">{{ record.lineNo ?? '-' }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_slipSid') }}</span><span class="card-value mono">{{ record.slipSid || '-' }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_slipGrpSid') }}</span><span class="card-value mono">{{ record.slipGrpSid || '-' }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_nidsRaw') }}</span><span class="card-value mono" style="word-break: break-all;">{{ record.nidsRaw || '-' }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_companyId') }}</span><span class="card-value mono">{{ record.companyId || '-' }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_ecountHid') }}</span><span class="card-value mono">{{ record._ecountHid ?? '-' }}</span></div>
        <div v-if="record.remark" class="card-field card-remark"><span class="card-label">{{ $t('ecountEtl_transactions_col_remark') }}</span><span class="card-value">{{ record.remark }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_inputAt') }}</span><span class="card-value">{{ formatDateTime(record.inputAt) }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_createdAt') }}</span><span class="card-value">{{ formatDateTime(record.createdAt) }}</span></div>
        <div class="card-field"><span class="card-label">{{ $t('ecountEtl_transactions_col_updatedAt') }}</span><span class="card-value">{{ formatDateTime(record.updatedAt) }}</span></div>
      </div>
    </div>

    <!-- 페이지네이션 (분리, 우측 정렬, show-total + size-changer) -->
    <div class="pagination-row">
      <a-pagination
        v-model:current="pagination.current"
        :total="pagination.total"
        :page-size="pageSize"
        :show-total="totalLabel"
        show-size-changer
        :page-size-options="['10', '20', '50', '100']"
        @change="onPageChange"
        @show-size-change="onPageSizeChange"
      />
    </div>

    <shortcut-help v-model:visible="showShortcutHelp" />

    <!-- SPEC-CHINA-ECOUNT-TX-LINK-001 파트 B: 연결키 설명 모달 -->
    <ecount-tx-link-info-modal
      :visible="showTxLinkModal"
      @close="showTxLinkModal = false"
    />
  </div>
</template>

<script>
// @MX:SPEC: SPEC-ECOUNT-FE-POLISH-001 Phase B-2

import { useKstTime } from './composables/useKstTime.js';
import { getTransactions, getTransactionsCustomersDistinct, getTransactionsStats, extractApiError, parsePaginatedResponse } from '../../apis/ecountEtl.js';
import EcountTxLinkInfoModal from '../../components/DealerGsp/EcountTxLinkInfoModal.vue';
import TransactionsDashboard from './components/TransactionsDashboard.vue';

// REQ-FE-T-006: 300ms debounce 유틸 (SalesOutbounds.vue 패턴 답습)
function debounce(fn, delay) {
  let timer;
  const debounced = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
  debounced.cancel = () => clearTimeout(timer);
  return debounced;
}
import RunStatusBadge from './_components/RunStatusBadge.vue';
import TimezoneToggle from './_components/TimezoneToggle.vue';
import ShortcutHelp from './_components/ShortcutHelp.vue';
import { RouterEnum } from '../../enums/router.enums.js';
import dealerEcountListMixin from '@/mixins/dealerEcountListMixin';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';

export default {
  name: 'EcountEtlTransactionsList',
  components: { RunStatusBadge, TimezoneToggle, ShortcutHelp, QuestionCircleOutlined, TransactionsDashboard, EcountTxLinkInfoModal },
  mixins: [dealerEcountListMixin],

  setup() {
    const { currentTz, toggleTz } = useKstTime();
    return { currentTz, toggleTz };
  },

  data() {
    return {
      loading: false,
      error: null,
      showShortcutHelp: false,
      showTxLinkModal: false,
      transactions: [],
      pageSize: 20,
      pagination: { current: 1, total: 0 },
      // REQ-FE-T-001: customerCode 단일 제거, customerCodes[] + ecountProductCode + transactionType 추가
      // REQ-FE-LINK-006: lotNumber 검색 필터 추가 (SPEC-CHINA-LOT-BATCH-TRACEABILITY-001)
      filters: { keyword: '', status: undefined, dateRange: null, customerCodes: [], ecountProductCode: '', transactionType: '', lotNumber: '', lotNumberPrefix: false },
      // 고객사 multi-select 상태 (SPEC-CHINA-ECOUNT-PRODUCTS-TRANSACTIONS-DASHBOARD-001)
      customerOptions: [],
      customersLoading: false,
      customersLoadError: false,
      // 대시보드 stats 상태
      statsData: null,
      statsLoading: false,
      statsErrorMsg: '',
    };
  },

  computed: {
    columns() {
      const dateColTitle = (labelKey) => () => (
        <span style="display: inline-flex; align-items: center; gap: 4px;">
          {this.$t(labelKey)}
          <a-tooltip title={this.$t('dealerGsp_tz_tooltip', { tz: this.timezoneInfo.label })}>
            <QuestionCircleOutlined style="color: rgba(0, 0, 0, 0.45); cursor: help" />
          </a-tooltip>
        </span>
      );
      // serial_lot_transactions 풀필드 노출
      return [
        { key: 'voucherNo', dataIndex: 'voucherNo', title: this.$t('ecountEtl_transactions_col_voucherNo'), width: 90, align: 'right' },
        { key: 'transactionDate', dataIndex: 'transactionDate', title: dateColTitle('ecountEtl_transactions_col_transactionDate'), width: 170 },
        { key: 'transactionDirection', dataIndex: 'transactionDirection', title: this.$t('ecountEtl_transactions_col_transactionDirection'), width: 100, align: 'center' },
        { key: 'transactionTypeEnum', dataIndex: 'transactionTypeEnum', title: this.$t('ecountEtl_transactions_col_transactionTypeEnum'), width: 180, ellipsis: true },
        { key: 'transactionType', dataIndex: 'transactionType', title: this.$t('ecountEtl_transactions_col_transactionType'), width: 100, align: 'center' },
        { key: 'transactionTypeCode', dataIndex: 'transactionTypeCode', title: this.$t('ecountEtl_transactions_col_transactionTypeCode'), width: 100 },
        { key: 'ecountProductCode', dataIndex: 'ecountProductCode', title: this.$t('ecountEtl_transactions_col_ecountProductCode'), width: 160 },
        { key: 'productDescription', dataIndex: 'productDescription', title: this.$t('ecountEtl_transactions_col_productDescription'), ellipsis: true, width: 240 },
        { key: 'gtinCode', dataIndex: 'gtinCode', title: this.$t('ecountEtl_transactions_col_gtinCode'), width: 150 },
        { key: 'gtin14', dataIndex: 'gtin14', title: this.$t('ecountEtl_transactions_col_gtin14'), width: 160 },
        { key: 'gs1CompanyPrefix3', dataIndex: 'gs1CompanyPrefix3', title: this.$t('ecountEtl_transactions_col_gs1CompanyPrefix3'), width: 90, align: 'center' },
        { key: 'gtinChecksumValid', dataIndex: 'gtinChecksumValid', title: this.$t('ecountEtl_transactions_col_gtinChecksumValid'), width: 100, align: 'center' },
        { key: 'mfgDate', dataIndex: 'mfgDate', title: this.$t('ecountEtl_transactions_col_mfgDate'), width: 170 },
        { key: 'lotNumber', dataIndex: 'lotNumber', title: this.$t('ecountEtl_transactions_col_lotNumber'), width: 100 },
        { key: 'qty', dataIndex: 'qty', title: this.$t('ecountEtl_transactions_col_qty'), width: 80, align: 'right' },
        { key: 'customerName', dataIndex: 'customerName', title: this.$t('ecountEtl_transactions_col_customerName'), width: 180, ellipsis: true },
        { key: 'customerCode', dataIndex: 'customerCode', title: this.$t('ecountEtl_transactions_col_customerCode'), width: 140 },
        { key: 'employeeCode', dataIndex: 'employeeCode', title: this.$t('ecountEtl_transactions_col_employeeCode'), width: 90 },
        { key: 'inputUserId', dataIndex: 'inputUserId', title: this.$t('ecountEtl_transactions_col_inputUserId'), width: 110 },
        { key: 'ioDateNo', dataIndex: 'ioDateNo', title: this.$t('ecountEtl_transactions_col_ioDateNo'), width: 150 },
        { key: 'lineNo', dataIndex: 'lineNo', title: this.$t('ecountEtl_transactions_col_lineNo'), width: 70, align: 'right' },
        { key: 'slipSid', dataIndex: 'slipSid', title: this.$t('ecountEtl_transactions_col_slipSid'), width: 170, ellipsis: true },
        { key: 'slipGrpSid', dataIndex: 'slipGrpSid', title: this.$t('ecountEtl_transactions_col_slipGrpSid'), width: 170, ellipsis: true },
        { key: 'nidsRaw', dataIndex: 'nidsRaw', title: this.$t('ecountEtl_transactions_col_nidsRaw'), width: 220, ellipsis: true },
        { key: 'companyId', dataIndex: 'companyId', title: this.$t('ecountEtl_transactions_col_companyId'), width: 200, ellipsis: true },
        { key: '_ecountHid', dataIndex: '_ecountHid', title: this.$t('ecountEtl_transactions_col_ecountHid'), width: 110, align: 'right' },
        { key: '_orphan', dataIndex: '_orphan', title: this.$t('ecountEtl_transactions_col_orphan'), width: 90, align: 'center' },
        { key: 'isDeleted', dataIndex: 'isDeleted', title: this.$t('ecountEtl_transactions_col_isDeleted'), width: 80, align: 'center' },
        { key: 'remark', dataIndex: 'remark', title: this.$t('ecountEtl_transactions_col_remark'), ellipsis: true, width: 220 },
        // SPEC-CHINA-ECOUNT-TX-LINK-001 파트 C: matchKey 컬럼 (데이터 컬럼, 비고정)
        { key: 'matchKey', dataIndex: 'serialLotMatchKey', title: this.$t('ecountEtl_transactions_col_matchKey'), width: 200, ellipsis: true },
        { key: 'inputAt', dataIndex: 'inputAt', title: dateColTitle('ecountEtl_transactions_col_inputAt'), width: 170 },
        { key: 'createdAt', dataIndex: 'createdAt', title: dateColTitle('ecountEtl_transactions_col_createdAt'), width: 170 },
        { key: 'updatedAt', dataIndex: 'updatedAt', title: dateColTitle('ecountEtl_transactions_col_updatedAt'), width: 170 },
        // @MX:WARN: fixed:'right' 액션 컬럼은 반드시 columns 배열 끝에 연속 배치해야 함.
        // @MX:REASON: antd a-table는 fixed:'right' 뒤에 비고정 컬럼이 오면 sticky 좌표가 깨져 헤더/셀이 floating 됨.
        // SPEC-CHINA-ECOUNT-TX-LINK-001 파트 C: 거래상세 연결 버튼
        { key: 'linkSalesLineBtn', title: this.$t('ecountEtl_transactions_linkSalesLine_col'), width: 110, align: 'center', fixed: 'right' },
        // 유통 흐름 보기 버튼 컬럼 (lot/GTIN → /lot-status)
        { key: 'lotFlowBtn', title: this.$t('lotFlow_actions_viewFlow_col'), width: 120, align: 'center', fixed: 'right' },
      ];
    },
  },

  mounted() {
    this.applyQueryToFilters();
    // SPEC-CHINA-ECOUNT-DEALER-FILTER-URL-SYNC-001: 필터 ↔ URL query 양방향 동기화 등록
    this.setupQueryFilterSync();
    // REQ-FE-T-003: mount 시 distinct 1회 fetch
    this.fetchCustomers();
    // REQ-FE-T-006: mount 시 list + stats 병렬 호출
    this.fetchData();
    this.fetchStats();
    // REQ-FE-T-006: 필터 변경 → 300ms debounce
    this._debouncedRefresh = debounce(() => {
      this.pagination.current = 1;
      Promise.all([this.fetchData(), this.fetchStats()]);
    }, 300);
  },

  beforeUnmount() {
    if (this._debouncedRefresh) this._debouncedRefresh.cancel();
  },

  watch: {
    // REQ-FE-T-006: 필터 deep watch → debounce refresh
    filters: {
      deep: true,
      handler() {
        if (this._debouncedRefresh) this._debouncedRefresh();
      },
    },
  },

  methods: {
    onSearch() {
      if (this._debouncedRefresh) this._debouncedRefresh.cancel();
      this.pagination.current = 1;
      Promise.all([this.fetchData(), this.fetchStats()]);
    },
    onReset() {
      if (this._debouncedRefresh) this._debouncedRefresh.cancel();
      this.filters = { keyword: '', status: undefined, dateRange: null, customerCodes: [], ecountProductCode: '', transactionType: '', lotNumber: '', lotNumberPrefix: false };
      this.pagination.current = 1;
      Promise.all([this.fetchData(), this.fetchStats()]);
    },
    onClearFilters() {
      if (this._debouncedRefresh) this._debouncedRefresh.cancel();
      this.filters.customerCodes = [];
      this.filters.ecountProductCode = '';
      this.filters.transactionType = '';
      this.pagination.current = 1;
      Promise.all([this.fetchData(), this.fetchStats()]);
    },

    // REQ-FE-T-003: distinct customer fetch (mount 시 1회)
    async fetchCustomers() {
      this.customersLoading = true;
      this.customersLoadError = false;
      try {
        const res = await getTransactionsCustomersDistinct();
        const data = res?.data || [];
        // REQ-FE-T-004: label = {customerName} ({customerCode}), value = customerCode
        this.customerOptions = data.map((c) => ({
          value: c.customerCode,
          label: c.customerName ? `${c.customerName} (${c.customerCode})` : c.customerCode,
          searchText: `${c.customerName || ''} ${c.customerCode}`.toLowerCase(),
        }));
      } catch (_) {
        this.customersLoadError = true;
      } finally {
        this.customersLoading = false;
      }
    },

    // REQ-FE-T-004: 검색 — customerName + customerCode 모두 case-insensitive
    filterCustomerOption(input, option) {
      const q = (input || '').toLowerCase();
      return (option.searchText || option.label || '').toLowerCase().includes(q);
    },

    // 고객사 chip label 반환
    customerLabel(code) {
      const opt = this.customerOptions.find((o) => o.value === code);
      return opt ? opt.label : code;
    },

    // chip 닫기 — customerCode 제거
    removeCustomerCode(code) {
      const idx = this.filters.customerCodes.indexOf(code);
      if (idx !== -1) this.filters.customerCodes.splice(idx, 1);
    },

    // stats 조회 (REQ-FE-T-006)
    async fetchStats() {
      this.statsLoading = true;
      this.statsErrorMsg = '';
      try {
        const params = {};
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          params.dateFrom = this.filters.dateRange[0].format ? this.filters.dateRange[0].format('YYYY-MM-DD') : this.filters.dateRange[0].toISOString().slice(0, 10);
          params.dateTo = this.filters.dateRange[1].format ? this.filters.dateRange[1].format('YYYY-MM-DD') : this.filters.dateRange[1].toISOString().slice(0, 10);
        }
        if (this.filters.customerCodes && this.filters.customerCodes.length > 0) params.customerCodes = this.filters.customerCodes;
        if (this.filters.ecountProductCode) params.ecountProductCode = this.filters.ecountProductCode;
        if (this.filters.transactionType) params.transactionType = this.filters.transactionType;
        const res = await getTransactionsStats(params);
        this.statsData = res?.data || null;
      } catch (e) {
        const err = extractApiError(e);
        this.statsErrorMsg = err.message || this.$t('ecountEtl_common_error');
        this.statsData = null;
      } finally {
        this.statsLoading = false;
      }
    },

    // @MX:NOTE: [AUTO] ACF-E-T-3/4 — chart 클릭 → 필터 toggle (customerCode OR 멀티셀렉트, productCode/transactionType 단일 toggle)
    onChartFilter({ type, value }) {
      if (this._debouncedRefresh) this._debouncedRefresh.cancel();
      if (type === 'customerCode') {
        // REQ-FE-T-007: toggle add/remove
        const codes = [...(this.filters.customerCodes || [])];
        const idx = codes.indexOf(value);
        if (idx !== -1) codes.splice(idx, 1);
        else codes.push(value);
        this.filters.customerCodes = codes;
      } else if (type === 'productCode') {
        // REQ-FE-T-008: toggle
        this.filters.ecountProductCode = this.filters.ecountProductCode === value ? '' : value;
      } else if (type === 'transactionType') {
        // REQ-FE-T-009: toggle
        this.filters.transactionType = this.filters.transactionType === value ? '' : value;
      }
      this.pagination.current = 1;
      Promise.all([this.fetchData(), this.fetchStats()]);
    },

    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const params = {
          page: this.pagination.current,
          limit: this.pageSize,
          keyword: this.filters.keyword || undefined,
          status: this.filters.status || undefined,
        };
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          params.startDate = this.filters.dateRange[0].toISOString();
          params.endDate = this.filters.dateRange[1].toISOString();
        }
        // REQ-FE-T-001/002: customerCodes[] 배열 전송
        if (this.filters.customerCodes && this.filters.customerCodes.length > 0) {
          params.customerCodes = this.filters.customerCodes;
        }
        if (this.filters.ecountProductCode) params.ecountProductCode = this.filters.ecountProductCode;
        if (this.filters.transactionType) params.transactionType = this.filters.transactionType;
        // REQ-FE-LINK-006: lotNumber 검색 필터 전달 (SPEC-CHINA-LOT-BATCH-TRACEABILITY-001)
        if (this.filters.lotNumber) {
          params.lotNumber = this.filters.lotNumber;
          if (this.filters.lotNumberPrefix) params.lotNumberPrefix = true;
        }
        const res = await getTransactions(params);
        const parsed = parsePaginatedResponse(res);
        this.transactions = parsed.items || parsed.data || [];
        this.pagination.total = parsed.total ?? 0;
      } catch (e) {
        const err = extractApiError(e);
        const localized = this.$t(err.messageKey, err.messageParams);
        this.error = localized.startsWith('ecountEtl_') ? err.message : localized;
        this.transactions = [];
        this.pagination.total = 0;
      } finally {
        this.loading = false;
      }
    },
    rowClassName(record) {
      return record.orphan ? 'ecount-etl-orphan-row' : '';
    },
    // REQ-FE-LINK-001 + REQ-FE-LINK-007: lot-trace 라우팅 (encodeURIComponent)
    navigateToLotTrace(lotNumber) {
      if (!lotNumber) return;
      this.$router.push('/lot-trace/' + encodeURIComponent(lotNumber));
    },

    // 유통 흐름 보기: lot/GTIN으로 /lot-status 진입
    navigateToLotFlow(lotNumber, gtinCode) {
      const query = {};
      if (lotNumber) query.lotNumber = lotNumber;
      if (gtinCode) query.gtin = gtinCode;
      this.$router.push({ path: '/lot-status', query });
    },

    // applyQueryToFilters: query string으로 lotNumber 사전 설정
    applyQueryToFilters() {
      const q = this.$route.query || {};
      if (q.lotNumber) this.filters.lotNumber = String(q.lotNumber);
      if (q.ecountProductCode) this.filters.ecountProductCode = String(q.ecountProductCode);
    },

    drillToProduct(gtinCode) {
      if (!gtinCode) return;
      this.$router.push({
        name: RouterEnum.EcountEtl.Products.name,
        query: { keyword: gtinCode },
      });
    },

    // SPEC-CHINA-ECOUNT-TX-LINK-001 파트 C: serial_lot 행 → 거래상세(SalesLines) 딥링크
    navigateToSalesLine(record) {
      if (record.voucherNo == null || record.lineNo == null) return;
      this.$router.push({
        name: RouterEnum.EcountEtl.SalesLines.name,
        query: {
          voucherNo: String(record.voucherNo),
          lineNo: String(record.lineNo),
          // transactionDate(YYYYMMDD) — 연결키 조회 시 dateFrom/dateTo로 활용
          ...(record.transactionDate ? { date: record.transactionDate.slice(0, 10).replace(/-/g, '') } : {}),
        },
      });
    },
  },
};
</script>

<style scoped>
.page-header { display: flex; align-items: center; margin-bottom: 16px; }
.page-title { font-size: 20px; font-weight: 600; }
.title-tip-icon { color: rgba(0, 0, 0, 0.45); cursor: help; font-size: 16px; margin-left: 6px; }
.filter-actions { margin-bottom: 0; margin-left: auto; margin-right: 0; }

.dealer-filter-form :deep(.ant-form-item) { margin-bottom: 12px; }
.dealer-filter-form :deep(.ant-form-item:last-child) { margin-bottom: 0; }
.filter-actions :deep(.ant-space) { flex-wrap: nowrap; }

.toolbar-row {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px; padding: 0 4px;
}
.total-count { font-size: 13px; color: #666; }
.toolbar-controls { display: flex; gap: 8px; }

.pagination-row { display: flex; justify-content: flex-end; margin-top: 16px; }

/* 고아 트랜잭션 행 강조 (데스크탑) */
:deep(.ecount-etl-orphan-row) { background-color: #fff7e6; }
:deep(.ecount-etl-orphan-row:hover > td) { background-color: #ffe7ba !important; }

/* 모바일 카드뷰 */
.mobile-card-list { display: flex; flex-direction: column; gap: 12px; }
.empty-state { padding: 32px 0; text-align: center; }
.mobile-card {
  background: #fff; border: 1px solid #f0f0f0; border-radius: 8px;
  padding: 12px; box-shadow: 0 1px 2px rgba(0,0,0,0.04);
}
.mobile-card-orphan { background: #fff7e6; border-color: #ffe7ba; }
.card-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #f5f5f5; gap: 8px;
}
.card-header-left { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.card-id { font-family: monospace; font-weight: 600; color: #333; font-size: 13px; }
.card-field {
  display: flex; justify-content: space-between; align-items: baseline;
  font-size: 12px; padding: 4px 0;
}
.card-label { color: #888; flex-shrink: 0; margin-right: 8px; }
.card-value { color: #333; text-align: right; word-break: break-all; }
.card-gtin-btn { padding: 0 !important; height: auto !important; font-family: monospace; }
.card-matchkey-btn { padding: 0 !important; height: auto !important; font-family: monospace; font-weight: 600; }
.card-actions {
  display: grid; grid-template-columns: 1fr; gap: 4px;
  margin-top: 8px; padding-top: 8px; border-top: 1px solid #f5f5f5;
}
.card-actions :deep(.ant-btn) { width: 100%; }

/* SPEC-CHINA-ECOUNT-TX-LINK-001 파트 C */
.match-key-cell {
  font-family: 'Courier New', Courier, monospace;
  font-size: 11px;
  color: #722ed1;
  background: #f9f0ff;
  border-radius: 3px;
  padding: 1px 4px;
  display: inline-block;
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 767px) {
  .page-header { flex-wrap: wrap; gap: 8px; row-gap: 8px; }
  .toolbar-row { flex-direction: column; align-items: stretch; gap: 8px; }
  .toolbar-controls { justify-content: flex-end; }
  .pagination-row { justify-content: center; }
}
</style>
